import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import { AfterImportMappingNote } from "@/components/PendingMapping";   // Batch 27 · Add-on 3 · B (M39)
import api, { toErrMsg, UPLOAD_TIMEOUT_MS } from "@/lib/api";
import { uploadGLChunked, CHUNKED_UPLOAD_THRESHOLD } from "@/lib/chunkedUpload";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Upload, CheckCircle2, XCircle, Trash2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { LockedBanner } from "@/components/SignOffCard";
import CoaMappingTable from "@/components/CoaMappingTable";
import SourceBadge from "@/components/SourceBadge";
import PriorSourceLabel, { TbStoredNotUsedNotice, TbYearPicker } from "@/components/PriorSourceLabel";   // B35-2 / B35-3
import LayerLine from "@/components/LayerLine";   // Batch 32 · B32-1 — the layer sentence (was the LayerBadge pill)
import { fmtNumber, fmtCount } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const ACCEPTED = ".csv,.xlsx,.xls,.xlsm";

// Parse "Parsing rows… X / Y" or "Inserted X / Y" out of the job message
// so we can render a big row-count under the progress bar. Returns null
// when the message is a plain status like "Queued" or "Auto-mapping…".
function parseRowCount(msg) {
  if (!msg) return null;
  const m = String(msg).match(/([\d,]+)\s*\/\s*([\d,]+)/);
  if (!m) return null;
  const done = Number(m[1].replace(/,/g, ""));
  const total = Number(m[2].replace(/,/g, ""));
  if (!Number.isFinite(done) || !Number.isFinite(total) || total <= 0) return null;
  return { done, total };
}


// Format a rough remaining-time hint based on elapsed vs. progress.
// Returns "" until we have enough signal (>=5% and >=2s elapsed) so the
// number doesn't jitter wildly in the first tick.
function formatEta(job) {
  if (!job || job.status !== "running" || !job.started_at) return "";
  const pct = Number(job.progress_pct || 0);
  if (pct < 5 || pct >= 99) return "";
  const startedMs = Date.parse(job.started_at);
  if (!startedMs) return "";
  const elapsedSec = (Date.now() - startedMs) / 1000;
  if (elapsedSec < 2) return "";
  const remainingSec = Math.max(1, Math.round((elapsedSec * (100 - pct)) / pct));
  if (remainingSec < 60) return `~${remainingSec}s remaining`;
  const m = Math.floor(remainingSec / 60);
  const s = remainingSec % 60;
  return s ? `~${m}m ${s}s remaining` : `~${m}m remaining`;
}

export default function GLImport() {
  const colW = useResizableColumns({ storageKey: "gl-import-batches", defaults: [110, 110, 160, 130, 130, 130, 130], mins: [72, 72, 100, 90, 90, 90, 90] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  // HOTFIX dead links: ?tab=tb / ?tab=coa opens that tab (the Disclosure checklist "Open TB" link lands on the Trial Balance)
  const [searchParams] = useSearchParams();
  const initialTab = ["tb", "coa"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "import";
  const { current, refresh } = useEngagement();
  // Batch 30 · M74: a refused upload / commit stays on the page (EN + AR) until dismissed or the next upload — on live
  // the Preparer's refusal was a toast that flashed and vanished
  const [refusal, setRefusal] = useState("");
  const showRefusal = (err, fallback) => {
    const ar = lang === "ar" && err?.response?.data?.detail_ar;
    setRefusal(String(ar || toErrMsg(err, fallback)));
  };
  const [preview, setPreview] = useState(null);
  const nPv = (preview?.columns || []).length;   // Step 11d · add-on: the preview of the uploaded file, one width per file column
  const pvCols = useResizableColumns({ storageKey: `gl-import-preview-${nPv}`, defaults: Array(nPv).fill(150), mins: Array(nPv).fill(80) });
  const [map, setMap] = useState({});
  const [committing, setCommitting] = useState(false);
  const [job, setJob] = useState(null);
  const [lastImport, setLastImport] = useState(null);   // Batch 27 · Add-on 3 · B (M39)
  // Tick every second while a job is running so the ETA hint updates
  // even between poll cycles (poll is 1.2s; tick keeps the string fresh).
  const [, setEtaTick] = useState(0);
  const jobId = job?.id, jobRunning = job?.status === "running";   // Step 7c · E3: the two parts of job the tick reads
  useEffect(() => {
    if (!jobRunning) return;
    const iv = setInterval(() => setEtaTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [jobId, jobRunning]);
  const [tb, setTb] = useState(null);
  const [coa, setCoa] = useState([]);
  const [period, setPeriod] = useState("current");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [acceptNoVoucher, setAcceptNoVoucher] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const engId = current?.id;
  const loadCoa = useCallback(async () => {
    if (!engId) return;
    try {
      const [rCoa, rTb] = await Promise.all([
        api.get(`/coa/${engId}`),
        api.get(`/gl/${engId}/trial-balance`),
      ]);
      setCoa(Array.isArray(rCoa.data) ? rCoa.data : []);
      setTb(rTb.data && rTb.data.rows ? rTb.data : null);
    } catch (err) {
      // interceptor already showed a toast; keep the screen usable
      setCoa([]); setTb(null);
    }
  }, [engId]);

  useEffect(() => { loadCoa(); }, [loadCoa]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side extension guard
    const okExt = ["csv", "xlsx", "xls", "xlsm"].some((x) => f.name.toLowerCase().endsWith("." + x));
    if (!okExt) {
      toast.error(t("gl.unsupportedFormat"));
      e.target.value = "";
      return;
    }
    setUploading(true);
    setRefusal("");
    let willPoll = false;
    const applyPreview = (data) => {
      setPreview(data);
      setMap(data.suggested_map || {});
      toast.success(t("gl.loadedRows").replace("{n}", fmtCount(data.total_rows || 0)));
    };
    const pollJob = (jobId) => {
      willPoll = true;
      setJob({ id: jobId, status: "queued", progress_pct: 0,
               message: t("gl.parsingFile"), kind: "gl_preview" });
      const poll = async () => {
        try {
          const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
          setJob(jr.data);
          if (jr.data.status === "done") {
            applyPreview(jr.data.result || {});
            setJob(null); setUploading(false);
            return;
          }
          if (jr.data.status === "failed") {
            toast.error(jobErrorText(jr.data, lang, t, t("gl.previewFailed")));
            setJob(null); setUploading(false);
            return;
          }
          setTimeout(poll, 1200);
        } catch (err) {
          toast.error(toErrMsg(err, t("gl.previewFailed")));
          setJob(null); setUploading(false);
        }
      };
      setTimeout(poll, 400);
    };
    try {
      if (f.size >= CHUNKED_UPLOAD_THRESHOLD) {
        // Chunked upload — split file into 4 MB pieces so no single
        // request approaches Cloudflare's 120 s proxy read timeout.
        setJob({ id: "upload", status: "running", progress_pct: 0,
                 message: t("gl.uploadingChunks").replace("{mb}", (f.size / 1024 / 1024).toFixed(1)),
                 kind: "gl_upload" });
        const res = await uploadGLChunked(f, (pct, msg) => {
          setJob({ id: "upload", status: "running", progress_pct: pct,
                   message: msg, kind: "gl_upload" });
        });
        if (res && res.job_id) {
          pollJob(res.job_id);
        } else {
          toast.error(t("gl.uploadFinalizeNoJob"));
          setJob(null); setUploading(false);
        }
      } else {
        // Small files still POST /gl/preview directly (snappy inline
        // parse — same as before the chunked flow was added).
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/gl/preview", fd,
          { headers: { "Content-Type": "multipart/form-data" },
            timeout: UPLOAD_TIMEOUT_MS, _silent: true });
        if (r.data && r.data.job_id) {
          pollJob(r.data.job_id);
        } else {
          applyPreview(r.data);
        }
      }
    } catch (err) {
      showRefusal(err, t("gl.previewFailed"));
      setJob(null);
    } finally {
      if (!willPoll) setUploading(false);
      e.target.value = "";
    }
  };

  const doCommit = async () => {
    if (!current || !preview) return;
    if (!map.voucher_no && !acceptNoVoucher) {
      toast.error(t("gl.mapVoucherFirst"));
      return;
    }
    setCommitting(true);
    setRefusal("");
    try {
      const r = await api.post("/gl/commit", {
        engagement_id: current.id, column_map: map,
        // Prefer server-side staging id — falls back to inline rows if the
        // preview endpoint didn't return one (older backend).
        preview_id: preview.preview_id || undefined,
        rows: preview.preview_id ? [] : (preview.rows || []),
        source_file: preview.filename, period, replace_existing: replaceExisting,
      }, { _silent: true });
      const jobId = r.data.job_id;
      if (!jobId) {
        // Legacy synchronous response (safety fallback)
        toast.success(t("gl.importedEntries").replace("{n}", r.data.inserted?.toLocaleString?.() || "0"));
        setPreview(null); setReplaceExisting(false);
        await refresh(); await loadCoa();
        return;
      }
      setJob({ id: jobId, status: "queued", progress_pct: 0, message: t("gl.queuedMsg") });
      // Poll until done
      const poll = async () => {
        try {
          const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
          setJob(jr.data);
          if (jr.data.status === "done") {
            const res = jr.data.result || {};
            const balance = res.balanced ? t("gl.balancedTick") : t("gl.unbalancedX");
            if (res.replaced) {
              toast.success(t("gl.replacedRows")
                .replace("{del}", fmtCount(res.deleted_previous || 0))
                .replace("{ins}", fmtCount(res.inserted || 0))
                .replace("{b}", balance));
            } else {
              toast.success(t("gl.importedWithBalance")
                .replace("{n}", fmtCount(res.inserted || 0))
                .replace("{b}", balance));
            }
            setLastImport(res);             // Batch 27 · Add-on 3 · B: pending mapping applied / accounts still unmapped
            if (res.duplicate_lines) {      // Batch 13 · B5
              toast.warning(t("gl.dupLinesLine").replace("{n}", fmtCount(res.duplicate_lines)).replace("{g}", fmtCount(res.duplicate_groups || 0)));
            }
            setPreview(null); setReplaceExisting(false);
            setJob(null);
            await refresh(); await loadCoa();
            setCommitting(false);
            return;
          }
          if (jr.data.status === "failed") {
            toast.error(jobErrorText(jr.data, lang, t, t("gl.glCommitFailed")));
            setJob(null);
            setCommitting(false);
            return;
          }
          setTimeout(poll, 1200);
        } catch (e) {
          setJob(null); setCommitting(false);
        }
      };
      setTimeout(poll, 800);
    } catch (err) {
      showRefusal(err, t("gl.glCommitFailed"));
      setCommitting(false);
    }
  };

  const commit = () => {
    if (replaceExisting) setConfirmOpen(true);
    else doCommit();
  };

  const [tbPeriod, setTbPeriod] = useState("current");   // Batch 3 · L22
  const [tbNotice, setTbNotice] = useState(null);         // B35-3: the upload reply's "stored but not used" notice
  const uploadTbDirect = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !current) return;
    const okExt = ["csv", "xlsx", "xls", "xlsm"].some((x) => f.name.toLowerCase().endsWith("." + x));
    if (!okExt) { toast.error(t("gl.unsupportedFormat")); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("engagement_id", current.id);
    fd.append("period", tbPeriod);   // Batch 3 · L22 — current-year or prior-year trial balance
    setUploading(true);
    try {
      setRefusal("");
      setTbNotice(null);
      const r = await api.post("/tb/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" },
          timeout: UPLOAD_TIMEOUT_MS, _silent: true });
      // TB upload is now async — server returns {job_id, status}.
      // Poll /jobs/{id} the same way GL Import does.
      const jobId = r.data?.job_id;
      if (!jobId) {
        // Legacy sync response — treat as done immediately.
        toast.success(t("gl.importedTbRows").replace("{n}", r.data.inserted || 0));
        setTbNotice(r.data.notice || null);
        await loadCoa();
        return;
      }
      setJob({ id: jobId, status: "queued", progress_pct: 0, message: t("gl.queuedMsg") });
      const poll = async () => {
        try {
          const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
          setJob(jr.data);
          if (jr.data.status === "done") {
            const res = jr.data.result || {};
            toast.success(t("gl.importedTbRows").replace("{n}", fmtCount(res.inserted || 0)));
            setLastImport(res);             // Batch 27 · Add-on 3 · B
            setTbNotice(res.notice || null);   // B35-3: the worker path carries the notice in the job result
            setJob(null);
            await loadCoa();
            return;
          }
          if (jr.data.status === "failed") {
            toast.error(jobErrorText(jr.data, lang, t, t("gl.tbUploadFailed")));
            setJob(null);
            return;
          }
          setTimeout(poll, 1200);
        } catch { setJob(null); }
      };
      setTimeout(poll, 800);
    } catch (err) {
      showRefusal(err, t("gl.tbUploadFailed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const purgeGl = async () => {
    if (!current) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/gl/${current.id}`, { params: { period } });
      // Backend switches to background-job mode for purges >= 20k rows so
      // the 120 s Cloudflare proxy timeout never kicks in on a 400k-row
      // purge (Mongo delete_many at that size can take 30-60 s under
      // production load). Small purges still return the inline count.
      if (r.data && r.data.job_id) {
        const jobId = r.data.job_id;
        setJob({ id: jobId, status: "queued", progress_pct: 0,
                 message: t("gl.purging").replace("{n}", fmtCount(r.data.total_rows || 0)),
                 kind: "gl_delete" });
        const poll = async () => {
          try {
            const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
            setJob(jr.data);
            if (jr.data.status === "done") {
              const res = jr.data.result || {};
              toast.success(t("gl.deletedRows").replace("{n}", fmtCount(res.deleted || 0)).replace("{period}", period));
              setJob(null); setDeleting(false);
              await refresh(); await loadCoa();
              return;
            }
            if (jr.data.status === "failed") {
              toast.error(jobErrorText(jr.data, lang, t, t("gl.purgeFailed")));
              setJob(null); setDeleting(false);
              return;
            }
            setTimeout(poll, 1200);
          } catch (err) {
            toast.error(toErrMsg(err, t("gl.purgeFailed")));
            setJob(null); setDeleting(false);
          }
        };
        setTimeout(poll, 400);
      } else {
        toast.success(t("gl.deletedRows").replace("{n}", fmtCount(r.data.deleted)).replace("{period}", period));
        await refresh(); await loadCoa();
        setDeleting(false);
      }
    } catch (err) {
      toast.error(toErrMsg(err, t("gl.purgeFailed")));
      setDeleting(false);
    }
  };


  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  const rowsShown = preview?.preview_rows || preview?.rows?.slice(0, 30) || [];

  return (
    <div className="space-y-6" data-testid="gl-import-page">
      <LockedBanner engagement={current} />
      <AfterImportMappingNote result={lastImport} engId={current?.id} />
      {refusal && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
             data-testid="gl-refusal">
          <div>
            <div className="font-medium">{t("gl.refusedTitle")}</div>
            <div data-testid="gl-refusal-text">{refusal}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setRefusal("")} data-testid="gl-refusal-dismiss">{t("gl.dismiss")}</Button>
        </div>
      )}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("gl.title")} <SourceBadge source={tb?.source} className="ms-2 align-middle" /> <PriorSourceLabel source={tb?.prior_source} className="ms-1 align-middle" /></h1>
        <LayerLine screen="glImport" />
        <p className="text-sm text-muted-foreground">{t("gl.subtitle")}</p>
        {/* Batch 13 · B5 — the GL Import summary: lines stored more than once (counted at the last commit) */}
        {tb && tb.rows && (
          <p className="mt-1 text-[12px]" data-testid="gl-dup-lines">
            {tb.duplicate_lines?.lines
              ? <span className="text-amber-800">{t("gl.dupLinesLine").replace("{n}", fmtCount(tb.duplicate_lines.lines)).replace("{g}", fmtCount(tb.duplicate_lines.groups))}</span>
              : <span className="text-muted-foreground">{tb.duplicate_lines ? t("gl.dupLinesNone") : ""}</span>}
          </p>
        )}
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList>
          <TabsTrigger value="import" data-testid="tab-import">{t("gl.title")}</TabsTrigger>
          <TabsTrigger value="tb" data-testid="tab-tb">{t("gl.tbTitle")}</TabsTrigger>
          <TabsTrigger value="coa" data-testid="tab-coa">{t("gl.coa")}</TabsTrigger>
        </TabsList>

        {/* Async GL commit progress bar */}
        {job && (
          <Card className="mt-3 border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-900" data-testid="gl-job-progress">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">
                    {job.status === "queued" ? t("gl.queuedMsg")
                     : job.status === "running" ? job.message
                     : job.status === "failed" ? t("gl.failed")
                     : t("gl.done")}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{t("gl.jobLine").replace("{id}", job.id?.slice(0, 8) || "")}</div>
                </div>
                <div className="w-52">
                  <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                    <div className={`h-full transition-all ${job.status === "failed" ? "bg-red-500" : "bg-emerald-500"}`}
                         style={{ width: `${job.progress_pct || 0}%` }} />
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-[10px] tabular-nums text-muted-foreground">
                    {(() => {
                      const eta = formatEta(job);
                      return eta ? <span data-testid="gl-job-eta">{eta}</span> : <span />;
                    })()}
                    <span>{job.progress_pct || 0}%</span>
                  </div>
                </div>
              </div>
              {/* Live row-count counter — parsed out of the progress message. */}
              {(() => {
                const rc = parseRowCount(job.message);
                if (!rc) return null;
                return (
                  <div className="flex items-baseline gap-2 pt-1" data-testid="gl-job-rowcount">
                    <span className="text-3xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-200">
                      {fmtCount(rc.done)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t("gl.ofRows").replace("{n}", fmtCount(rc.total))}
                    </span>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        <TabsContent value="import" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">{t("gl.chooseFile")}</CardTitle>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" data-testid="gl-purge-btn">
                    <Trash2 className="me-1 h-3 w-3 text-red-600" />
                    <span className="text-xs">{t("gl.purge")} ({period})</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("gl.purgeTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("gl.purgeConfirm").replace("{period}", period)}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={purgeGl} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="gl-purge-confirm">
                      {deleting ? t("common.loading") : t("gl.confirmDelete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-4 rounded-md border bg-slate-50 p-3 dark:bg-slate-900/50">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("gl.period")}:</Label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="period" value="current" checked={period === "current"} onChange={() => setPeriod("current")} data-testid="period-current" />
                  <span>{t("gl.currentYear")} (FY{current.financial_year})</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="period" value="prior" checked={period === "prior"} onChange={() => setPeriod("prior")} data-testid="period-prior" />
                  <span>{t("gl.priorYear")} (FY{Number(current.financial_year) - 1})</span>
                </label>
                <div className="ms-auto flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 dark:bg-red-950/40 dark:border-red-900">
                  <input
                    type="checkbox"
                    id="replace-existing"
                    checked={replaceExisting}
                    onChange={(e) => setReplaceExisting(e.target.checked)}
                    data-testid="replace-existing"
                  />
                  <label htmlFor="replace-existing" className="cursor-pointer text-xs font-medium text-red-800 dark:text-red-200">
                    {t("gl.replaceExisting")}
                  </label>
                </div>
              </div>
              <div className="mb-2 text-xs text-muted-foreground" data-testid="gl-upload-mode">
                {replaceExisting ? t("gl.modeReplace") : t("gl.modeAdd")}   {/* Batch 8.4 · A1 (F-51): says plainly whether the upload REPLACES or ADDS */}
              </div>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 p-8 text-sm hover:border-emerald-500 hover:bg-emerald-50/40 dark:border-slate-700">
                <Upload className="h-5 w-5" />
                <span>{uploading ? t("common.loading") : t("gl.chooseFileFull").replace("{base}", t("gl.chooseFile"))}</span>
                <input type="file" accept={ACCEPTED} className="hidden" onChange={onFile} data-testid="gl-file-input" disabled={uploading} />
              </label>
              <div className="text-[11px] text-muted-foreground">
                {t("gl.rowLimitNote")}
              </div>
            </CardContent>
          </Card>

          {preview && !map.voucher_no && (
            <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
                  data-testid="voucher-warning-banner">
              <CardContent className="flex items-start gap-3 p-4">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-600 dark:text-amber-400" />
                <div className="flex-1 space-y-1">
                  <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                    {t("gl.voucherWarnTitle")}
                  </div>
                  <p className="text-xs text-amber-800 dark:text-amber-200/90">
                    {t("gl.voucherWarnBody1Pre")}
                    <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-[10px] dark:bg-amber-900/60">V-0001</code>{t("gl.voucherWarnBody1Post")}
                    <code className="ms-1 rounded bg-amber-100 px-1 py-0.5 font-mono text-[10px] dark:bg-amber-900/60">V-0002</code>{t("gl.voucherWarnBody1End")}
                    <b>{t("gl.voucherWarnBody1BoldEnd")}</b>{t("gl.voucherWarnBody1Tail")}
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-200/90">
                    <b>{t("gl.voucherWarnBody2BoldPre")}</b>{t("gl.voucherWarnBody2Post")}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {preview && (
            <>
              <Card>
                <CardHeader><CardTitle className="text-base">{t("gl.colMap")}</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {["entry_date", "voucher_no", "account_code", "account_name", "description", "debit", "credit", "vendor_customer", "category_hint", "posted_by", "approver", "posted_time", "journal_source"].map((k) => {   /* Batch 34 · Z-5: journal_source */
                    const isVoucher = k === "voucher_no";
                    const isMissing = isVoucher && !map[k];
                    return (
                    <div key={k}>
                      <Label className={`text-xs uppercase tracking-wider ${isMissing ? "text-amber-700 dark:text-amber-300" : ""}`}>
                        {k === "voucher_no" ? t("gl.voucherLabelInMap") :
                         k === "category_hint" ? t("gl.categoryAuto") :
                         k === "posted_by" ? t("gl.postedByOpt") :
                         k === "posted_time" ? t("gl.postedTimeOpt") :
                         k === "approver" ? t("gl.approverOpt") :   /* Batch 31 · item 12 (M95) */
                         k === "journal_source" ? t("b34e.journalSourceOpt") :   /* Batch 34 · Z-5 */
                         k}
                        {isMissing && <span className="ms-1 text-[9px] font-bold">{t("gl.requiredMark")}</span>}
                      </Label>
                      <Select value={map[k] || ""} onValueChange={(v) => setMap({ ...map, [k]: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid={`map-${k}`}
                                       className={isMissing ? "border-amber-500 ring-2 ring-amber-200 dark:border-amber-500 dark:ring-amber-900/50" : ""}>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {preview.columns.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );})}
                </CardContent>
                {!map.voucher_no && (
                  <div className="border-t bg-amber-50 px-6 py-3 text-xs dark:bg-amber-950/30"
                       data-testid="voucher-override-row">
                    <label className="flex cursor-pointer items-start gap-2">
                      <input type="checkbox"
                             className="mt-0.5"
                             checked={acceptNoVoucher}
                             onChange={(e) => setAcceptNoVoucher(e.target.checked)}
                             data-testid="accept-no-voucher" />
                      <span className="text-amber-900 dark:text-amber-100">
                        <b>{t("gl.voucherAckBoldPre")}</b>{t("gl.voucherAckPost")}<code className="mx-0.5 rounded bg-amber-100 px-1 font-mono dark:bg-amber-900/60">V-####</code>{t("gl.voucherAckTail")}
                      </span>
                    </label>
                  </div>
                )}
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">
                    {t("gl.previewCount").replace("{title}", t("gl.preview")).replace("{n}", fmtCount(preview.total_rows || preview.rows.length))}
                    <span className="ms-2 text-xs font-normal text-muted-foreground">
                      {t("gl.showingFirst").replace("{n}", rowsShown.length)}
                    </span>
                  </CardTitle>
                  <Button onClick={commit} disabled={committing} className="bg-slate-900 hover:bg-slate-800" data-testid="gl-commit-btn">
                    {t("gl.commit")}
                  </Button>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto max-h-[400px] overflow-y-auto sticky-header">
                  <ResizableTable cols={pvCols} className="w-full text-xs">
                    <thead className="border-b bg-slate-50 text-muted-foreground">
                      <tr>{preview.columns.map((c, i) => (<ResizableTh key={c} index={i} className="px-3 py-2 text-start">{c}</ResizableTh>))}</tr>
                    </thead>
                    <tbody>
                      {rowsShown.slice(0, 100).map((r, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50">
                          {preview.columns.map((c) => (
                            <td key={c} className="px-3 py-1.5">{String(r[c] ?? "")}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </ResizableTable>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="tb">
          <Card className="mb-4 border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/20 dark:border-emerald-900">
            <CardHeader>
              <CardTitle className="text-base">{t("gl.tbDirectTitle")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t("gl.tbDirectDescPre")}<b>{t("gl.tbDirectDescCols")}</b>
              </p>
              <TbYearPicker value={tbPeriod} onChange={setTbPeriod} disabled={uploading} />
              <TbStoredNotUsedNotice state={tb?.tb_stored_not_used} notice={tbNotice} />
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-emerald-300 p-4 text-sm hover:border-emerald-500 hover:bg-emerald-50/60">
                <Upload className="h-4 w-4" />
                <span>{uploading ? t("common.loading") : t("gl.uploadTbDirectBtn")}</span>
                <input type="file" accept={ACCEPTED} className="hidden"
                       onChange={uploadTbDirect}
                       data-testid="tb-direct-upload"
                       disabled={uploading} />
              </label>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">{t("gl.tbTitle")}</CardTitle>
              {tb && (
                <div className="flex items-center gap-2 text-xs">
                  {tb.balanced ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {t("gl.balanced")}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-red-700"><XCircle className="h-3 w-3" /> {t("gl.unbalanced")}</span>
                  )}
                  <span className="text-muted-foreground">{t("gl.tbDrCr").replace("{dr}", fmtNumber(Number(tb.total_debit || 0))).replace("{cr}", fmtNumber(Number(tb.total_credit || 0)))}</span>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0 max-h-[600px] overflow-y-auto sticky-header">
              {(!tb || !tb.rows) && (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {t("gl.noGlDataYet")}
                </div>
              )}
              {tb && tb.rows && (
                <ResizableTable cols={colW} wrap className="w-full text-sm">
                  <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
                    <tr>
                      <ResizableTh index={0} className="px-4 py-2 text-start">{t("gl.tbColCode")}</ResizableTh>
                      <ResizableTh index={1} className="px-4 py-2 text-start">{t("gl.tbColAccount")}</ResizableTh>
                      <ResizableTh index={2} className="px-4 py-2 text-start">{t("gl.tbColCategory")}</ResizableTh>
                      <ResizableTh index={3} className="px-4 py-2 text-end">{t("gl.tbColDebit")}</ResizableTh>
                      <ResizableTh index={4} className="px-4 py-2 text-end">{t("gl.tbColCredit")}</ResizableTh>
                      <ResizableTh index={5} className="px-4 py-2 text-end">{t("gl.tbColNet")}</ResizableTh>
                      {tb.prior_year_available && <ResizableTh index={6} className="px-4 py-2 text-end" data-testid="tb-py-col">{t("b35.pyNet")}</ResizableTh>}
                    </tr>
                  </thead>
                  <tbody>
                    {tb.rows.map((r, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50">
                        <td className="px-4 py-2">{r.account_code}</td>
                        <td className="px-4 py-2 font-medium">{r.account_name}</td>
                        <td className="px-4 py-2"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs">{r.category}</span></td>
                        <td className="px-4 py-2 num-cell">{fmtNumber(Number(r.debit || 0))}</td>
                        <td className="px-4 py-2 num-cell">{fmtNumber(Number(r.credit || 0))}</td>
                        <td className={`px-4 py-2 num-cell font-medium ${r.net < 0 ? "text-red-600" : ""}`}>{fmtNumber(r.net)}</td>
                        {tb.prior_year_available && <td className="px-4 py-2 num-cell">{fmtNumber(Number(r.py_net || 0))}</td>}
                      </tr>
                    ))}
                  </tbody>
                </ResizableTable>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coa">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-xs dark:border-emerald-900 dark:bg-emerald-950/20"
             data-testid="coa-fs-mapping-hint">
          <div>
            <b>{t("gl.coaHintPre")}</b>
          </div>
          <Link to={`/ifrs/${current.id}/mapping`}>
            <Button size="sm" variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="coa-open-fs-mapping">
              <ExternalLink className="me-1 h-3 w-3" /> {t("gl.openFsMappingBtn")}
            </Button>
          </Link>
        </div>
        <CoaMappingTable engagementId={current.id} coa={coa} reload={loadCoa} />
      </TabsContent>
      </Tabs>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("gl.replaceConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("gl.replaceConfirmDesc").replace("{period}", period).replace("{count}", fmtCount(preview?.total_rows || 0))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doCommit} disabled={committing} className="bg-red-600 hover:bg-red-700" data-testid="gl-replace-confirm">
              {committing ? t("common.loading") : t("gl.confirmReplace")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
